import { useGetQueuesByAccountIdQuery } from '@/services/queues';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import {
  AccountMilestoneStatus,
  AccountMilestoneWithRequirements,
  AccountRequirementDocument,
  AccountRequirementStatus,
  AccountStipulationType,
  Flag,
  FlagPurposeType,
  MilestoneRequirement,
  MilestoneType,
  PackageRejectionInfo,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
} from 'types';

import { useGetAccountQuery, useGetAccountStipulationsQuery } from '../../services/accounts';
import { useGetChangeOrdersQuery } from '../../services/changeOrders';
import RejectionBanner from '../Reject/RejectionBanner';
import RejectionBannerV2 from '../Reject/RejectionBannerV2';

import ChangeOrderBanner from './ChangeOrderBanner';
import { resolveChangeOrderBannerLabel } from './changeOrderBannerState';
import { MilestoneCompletionProps, resolveMilestoneCompletionBanner } from './milestoneBannerState';
import MilestoneCompletionBanner from './MilestoneCompletionBanner';
import PtoRejectionBanner from './PtoRejectionBanner';

const getMostRecentObj = <T,>(array: T[], dateField: string = 'date'): T => {
  return array.reduce((prev, current) => {
    return (prev as Record<string, any>)[dateField] > (current as Record<string, any>)[dateField] ? prev : current;
  });
};

const getPreviousCompletedMilestones = (
  milestones: AccountMilestoneWithRequirements[],
): AccountMilestoneWithRequirements[] => {
  if (!milestones) return [];
  return milestones.filter(
    (milestone: AccountMilestoneWithRequirements) => milestone.status === AccountMilestoneStatus.approved,
  );
};

const SpecificLanguageWhenDomesticContentIsRejected =
  'LightReach review determined that this project does not satisfy the Domestic Content equipment thresholds. If you would like to re-review of ITC-DC Qualification, please provide additional document(s) when resubmitting the Install Package.';

type BannerState =
  | { type: 'none' }
  | { type: 'ptoRejected'; accountId: string }
  | { type: 'milestoneCompletion'; props: MilestoneCompletionProps }
  | { type: 'packageRejection'; banners: PackageRejectionBanner[] }
  | { type: 'changeOrder'; label: string };

interface PackageRejectionBanner {
  key: string;
  useFlagDriven: boolean;
  milestone: MilestoneType;
  milestoneName: string;
  rejections?: string[];
  rejectedAt?: Date;
  flaggedItems: Flag[];
  rejectionInfo?: PackageRejectionInfo;
  additionalRejectionContext?: string;
}

const useAccountBannerData = (accountId: string): BannerState => {
  const {
    flagDrivenPackageReview,
    showPtoRejectionBanner,
    ptoQueue: usePtoQueue = false,
    ptoGreenlight = false,
    easyChangeOrder = false,
  } = useFlags();
  const { data: account } = useGetAccountQuery(accountId);
  const { data: stipulations } = useGetAccountStipulationsQuery(accountId, { skip: !accountId });
  const { data: changeOrders } = useGetChangeOrdersQuery(accountId, { skip: !accountId || !easyChangeOrder });

  const { data: queuesData } = useGetQueuesByAccountIdQuery({
    accountId,
    types: [
      QueueType.installPackage,
      QueueType.activationPackage,
      QueueType.domesticContent,
      QueueType.permissionToOperate,
      QueueType.loopFieldDesignDocuments,
    ],
  });
  const installationQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);
  const activationQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.activationPackage);
  const domesticContentQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.domesticContent);
  const ptoQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.permissionToOperate);
  const loopFieldDesignDocsQueue = queuesData?.find(
    (q: QueueDocumentResponse) => q.type === QueueType.loopFieldDesignDocuments,
  );

  const hasUnsatisfiedStipulations = useMemo(() => {
    if (!stipulations?.length) {
      return false;
    }

    const unsatisfiedStipulations = stipulations.filter(
      (stipulation) =>
        !stipulation.isSatisfied &&
        [
          AccountStipulationType.administrative,
          AccountStipulationType.designVerification,
          AccountStipulationType.changeOrder,
        ].includes(stipulation.stipulationType),
    );

    return unsatisfiedStipulations.length > 0;
  }, [stipulations]);

  const installMilestone: AccountMilestoneWithRequirements = account?.milestones?.find(
    (milestone: AccountMilestoneWithRequirements) => milestone.type === MilestoneType.install,
  );
  const activationMilestone: AccountMilestoneWithRequirements = account?.milestones?.find(
    (milestone: AccountMilestoneWithRequirements) => milestone.type === MilestoneType.activation,
  );

  const installationFlags: Flag[] = installationQueue?.flags ?? [];
  const activationFlags: Flag[] = activationQueue?.flags ?? [];
  const installationConditionallyApprovedFlags: Flag[] =
    installationQueue?.status === QueueStatus.conditionallyApproved
      ? installationFlags.filter((flag: Flag) => !flag.resolved && flag.purposeType === FlagPurposeType.stipulation)
      : [];
  const combinedFlags: Flag[] = [...activationFlags, ...installationConditionallyApprovedFlags];

  const installRejections = useMemo(() => {
    if (!installMilestone || installMilestone.status !== AccountMilestoneStatus.rejected) {
      return;
    }
    const installPackage = installMilestone.requirements.find(
      (req: AccountRequirementDocument) => req.type === MilestoneRequirement.installPackage,
    );
    const rejectedEvents = installPackage?.statusTimeline?.filter(
      (status) => status.newStatus === AccountRequirementStatus.rejected,
    );
    const rejectedAt =
      rejectedEvents && rejectedEvents?.length > 0
        ? getMostRecentObj(rejectedEvents, 'modifiedAt')?.modifiedAt
        : undefined;
    const rejectedRequirements = installPackage?.requirements
      .filter((req: AccountRequirementDocument) => req.status === AccountRequirementStatus.rejected)
      .map((rejectedRequirement: AccountRequirementDocument) => rejectedRequirement.name);
    return { rejections: rejectedRequirements, rejectedAt };
  }, [installMilestone]);

  const activationRejections = useMemo(() => {
    if (!activationMilestone || activationMilestone.status !== AccountMilestoneStatus.rejected) {
      return;
    }
    const activationPackage = activationMilestone.requirements.find(
      (req: AccountRequirementDocument) => req.type === MilestoneRequirement.activationPackage,
    );
    const rejectedEvents = activationPackage?.statusTimeline?.filter(
      (status) => status.newStatus === AccountRequirementStatus.rejected,
    );
    const rejectedAt =
      rejectedEvents && rejectedEvents?.length > 0
        ? getMostRecentObj(rejectedEvents, 'modifiedAt')?.modifiedAt
        : undefined;
    const rejectedRequirements = activationPackage?.requirements
      .filter((req: AccountRequirementDocument) => req.status === AccountRequirementStatus.rejected)
      .map((rejectedRequirement: AccountRequirementDocument) => rejectedRequirement.name);
    return { rejections: rejectedRequirements, rejectedAt };
  }, [activationMilestone]);

  const domesticContentAdditionalRejectionContext: string | undefined = useMemo(() => {
    if (!domesticContentQueue || domesticContentQueue?.status !== QueueStatus.rejected) {
      return;
    }
    return SpecificLanguageWhenDomesticContentIsRejected;
  }, [domesticContentQueue]);

  if (hasUnsatisfiedStipulations) {
    return { type: 'none' };
  }

  if (showPtoRejectionBanner && ptoQueue?.status === QueueStatus.rejected) {
    return { type: 'ptoRejected', accountId };
  }

  const milestonesCompleted = getPreviousCompletedMilestones(account?.milestones);
  const latestCompletedMilestone =
    milestonesCompleted.length > 0 ? getMostRecentObj(milestonesCompleted, 'completedAt') : undefined;
  const noRejectionOnPackage = !installRejections?.rejections?.length && !activationRejections?.rejections?.length;

  const milestoneBanner = resolveMilestoneCompletionBanner({
    account,
    latestCompletedMilestone,
    ptoQueue,
    hasDesignDocsQueue: Boolean(loopFieldDesignDocsQueue),
    noRejectionOnPackage,
    flags: { flagDrivenPackageReview, usePtoQueue, ptoGreenlight },
  });

  if (milestoneBanner.visible) {
    return { type: 'milestoneCompletion', props: milestoneBanner.props };
  }

  const rejectionBanners: PackageRejectionBanner[] = [];
  const mostRecentPackageRejectionInfo = activationQueue?.rejectionReasons?.length
    ? (getMostRecentObj(activationQueue.rejectionReasons) as { rejectionInfo?: PackageRejectionInfo }).rejectionInfo
    : undefined;

  if (installRejections?.rejections?.length) {
    rejectionBanners.push({
      key: 'install-rejected-banner',
      useFlagDriven: !!flagDrivenPackageReview,
      milestone: MilestoneType.install,
      milestoneName: 'Installation Package',
      rejections: installRejections.rejections,
      rejectedAt: installRejections.rejectedAt,
      flaggedItems: installationFlags,
      additionalRejectionContext: domesticContentAdditionalRejectionContext,
    });
  }

  if (activationRejections?.rejections?.length) {
    rejectionBanners.push({
      key: 'activation-rejected-banner',
      useFlagDriven: !!flagDrivenPackageReview,
      milestone: MilestoneType.activation,
      milestoneName: 'Activation Package',
      rejections: activationRejections.rejections,
      rejectedAt: activationRejections.rejectedAt,
      flaggedItems: combinedFlags,
      rejectionInfo: mostRecentPackageRejectionInfo,
    });
  }

  if (rejectionBanners.length > 0) {
    return { type: 'packageRejection', banners: rejectionBanners };
  }

  const changeOrderBannerLabel = easyChangeOrder ? resolveChangeOrderBannerLabel(changeOrders) : undefined;
  if (changeOrderBannerLabel) {
    return { type: 'changeOrder', label: changeOrderBannerLabel };
  }

  return { type: 'none' };
};

interface AccountBannersProps {
  accountId: string;
}

const AccountBanners = ({ accountId }: AccountBannersProps) => {
  const bannerState = useAccountBannerData(accountId);

  switch (bannerState.type) {
    case 'ptoRejected':
      return <PtoRejectionBanner accountId={bannerState.accountId} />;

    case 'milestoneCompletion':
      return <MilestoneCompletionBanner {...bannerState.props} />;

    case 'packageRejection':
      return (
        <>
          {bannerState.banners.map((banner) =>
            banner.useFlagDriven ? (
              <RejectionBannerV2
                key={banner.key}
                milestone={{ type: banner.milestone, name: banner.milestoneName }}
                flaggedItems={banner.flaggedItems}
                rejectedAt={banner.rejectedAt}
              />
            ) : (
              <RejectionBanner
                key={banner.key}
                milestone={banner.milestone}
                rejections={banner.rejections ?? []}
                rejectionInfo={banner.rejectionInfo}
                additionalRejectionContext={banner.additionalRejectionContext}
              />
            ),
          )}
        </>
      );

    case 'changeOrder':
      return <ChangeOrderBanner label={bannerState.label} />;

    case 'none':
    default:
      return null;
  }
};

export default AccountBanners;
